import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
import { noteBodySchema, recordEntitySchema, RECORD_ENTITIES, } from './notes-tasks.js';
import { TEMPLATE_FIELDS, templatePlaceholders } from './templates.js';
/**
 * Note templates: a named rich text body a user inserts while writing a note, offered
 * on every kind of record (`global`) or on the kinds in `entityTypes`. Inserting copies the body
 * with its merge fields filled in, in the browser, from the record the note goes on; the note
 * keeps no link to the template.
 */
/** The template entity a record kind's own merge fields use (`{{job.title}}` on a job). */
export const RECORD_TEMPLATE_ENTITY = {
    candidates: 'candidate',
    companies: 'company',
    contacts: 'contact',
    projects: 'project',
    deals: 'deal',
    jobs: 'job',
};
/**
 * Merge fields every note can use, whatever its record: the record's name, the writer's name and
 * today's date. The only ones a global template, or one on several kinds, may hold.
 */
export const NOTE_CONTEXT_FIELDS = ['record.name', 'me.name', 'today.date'];
export const noteTemplateNameSchema = z.string().trim().min(1, 'A template needs a name').max(120);
const scopeShape = {
    name: noteTemplateNameSchema,
    /** HTML, sanitised by the API as a note's body is. */
    body: noteBodySchema,
    /** A new note started from it has its Pin box ticked. */
    pinnedByDefault: z.boolean(),
    /** Offered on every kind of record, including kinds added later. */
    global: z.boolean(),
    /** The kinds it is offered on when not global; empty when global. */
    entityTypes: z
        .array(recordEntitySchema)
        .max(RECORD_ENTITIES.length)
        .refine((kinds) => new Set(kinds).size === kinds.length, 'Each kind of record only once'),
};
/** A template is global XOR offered on at least one kind. */
export function noteTemplateScopeProblem(scope) {
    if (scope.global && scope.entityTypes.length > 0)
        return 'A global template is offered everywhere: leave the record kinds empty';
    if (!scope.global && scope.entityTypes.length === 0)
        return 'Choose at least one kind of record, or make the template global';
    return null;
}
export const noteTemplateInputSchema = z
    .object({
    ...scopeShape,
    pinnedByDefault: scopeShape.pinnedByDefault.default(false),
    global: scopeShape.global.default(false),
    entityTypes: scopeShape.entityTypes.default([]),
})
    .superRefine((t, ctx) => {
    const problem = noteTemplateScopeProblem(t);
    if (problem)
        ctx.addIssue({ code: 'custom', message: problem, path: ['entityTypes'] });
});
/** The scope is checked by the route on the template as it would be after the patch. */
export const noteTemplatePatchSchema = z
    .object(scopeShape)
    .partial()
    .refine((p) => Object.keys(p).length > 0, 'Nothing to change');
export const noteTemplateSchema = z.object({
    id: idSchema,
    ...scopeShape,
    createdAt: instantSchema,
    updatedAt: instantSchema,
});
/**
 * The merge fields a template names that a note on one of its kinds could not fill: a context
 * field always can; any other must be a field of EVERY kind the template covers, which only a
 * template on exactly that one kind satisfies. `customKeys` are the live custom field keys of
 * each kind. A field that is retired later renders empty instead.
 */
export function unknownNoteTemplateFields(body, scope, customKeys) {
    return templatePlaceholders(body).filter((path) => {
        if (NOTE_CONTEXT_FIELDS.includes(path))
            return false;
        if (scope.global || scope.entityTypes.length !== 1)
            return true;
        const kind = scope.entityTypes[0];
        const [entity, field, key] = path.split('.');
        if (entity !== RECORD_TEMPLATE_ENTITY[kind])
            return true;
        if (field === 'custom')
            return !key || !(customKeys[kind] ?? []).includes(key);
        return key !== undefined || !TEMPLATE_FIELDS[RECORD_TEMPLATE_ENTITY[kind]].includes(field);
    });
}
//# sourceMappingURL=note-templates.js.map